<template>
  <!--
    Tracking (card https://trello.com/c/AdJtbgH6). The ONE renderer for an
    order's shipments: the portal's OrderDetailView and the storefront's
    StorefrontOrderDetail both mount it, so the rules below cannot drift
    between the buyer's two views of the same order.

    One order can carry SEVERAL shipments: MVP routes an order to a single
    decorator, but that decorator may ship it in multiple boxes, each with
    its own tracking number. Every box is listed -- a buyer told about only
    the last of three cannot find the other two.

    `shipments` is tri-state on purpose:
      - an array with rows: list them;
      - `[]`: we KNOW nothing has shipped -- a neutral "not yet" line;
      - `null`: we do not know (the read failed, or the payload predates
        tracking) -- render nothing, because "no tracking yet" would be a
        claim we cannot back.
  -->
  <div v-if="Array.isArray(shipments)">
    <slot name="label">
      <div class="text-subtitle-2 text-grey-darken-1">Tracking</div>
    </slot>
    <div v-if="shipments.length" data-testid="order-shipments">
      <div
        v-for="(shipment, i) in shipments"
        :key="`${shipment.carrier}-${shipment.tracking_number}`"
        class="text-body-2"
        :data-testid="`order-shipment-${i}`"
      >
        <span
          v-if="shipment.carrier"
          class="mr-1"
          :data-testid="`order-shipment-carrier-${i}`"
          >{{ shipment.carrier }}</span
        >
        <!--
          A link ONLY for an http(s) URL the API supplied. We never build a
          carrier deep link from a guessed template (the backend owns that),
          and we never hand the browser a javascript:/data:/relative href,
          even though the write path validates the scheme -- this component
          renders every writer's rows, not just that one's.
        -->
        <a
          v-if="isWebUrl(shipment.tracking_url)"
          :href="shipment.tracking_url"
          target="_blank"
          rel="noopener noreferrer"
          :data-testid="`order-shipment-link-${i}`"
          >{{ shipment.tracking_number }}</a
        >
        <span v-else :data-testid="`order-shipment-number-${i}`">{{
          shipment.tracking_number
        }}</span>
        <span
          v-if="shippedOn(shipment.shipped_at)"
          class="text-medium-emphasis ml-1"
          :data-testid="`order-shipment-date-${i}`"
          >&middot; Shipped {{ shippedOn(shipment.shipped_at) }}</span
        >
      </div>
    </div>
    <div
      v-else
      class="text-body-2 text-medium-emphasis"
      data-testid="order-shipments-empty"
    >
      No tracking information yet.
    </div>
  </div>
</template>

<script>
// An explicit zone designator at the end of an ISO timestamp: "Z" or +hh:mm.
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

export default {
  name: "OrderShipments",
  props: {
    // Rows from OrderShipment.to_public_dict(): carrier, tracking_number,
    // tracking_url, shipped_at. `null` means unknown -- see the template.
    shipments: {
      type: Array,
      default: null,
    },
  },
  methods: {
    isWebUrl(url) {
      return typeof url === "string" && /^https?:\/\/[^/]/i.test(url.trim());
    },
    /**
     * The calendar day the box shipped, or "" when there is none to show.
     *
     * `shipped_at` is written with datetime.utcnow and serialized without a
     * zone, so a naive value is UTC. `new Date()` would read it as LOCAL
     * time, which puts an evening shipment on the next day for a US shopper;
     * appending "Z" parses it as the instant it is, shown in the shopper's
     * own time zone.
     */
    shippedOn(iso) {
      if (typeof iso !== "string" || !iso) return "";
      const d = new Date(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>
